import { useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/layout/PageHeader';
import {
  usePaymentDetails,
  type PaymentBillingAddress,
  type PaymentBillingDetails,
  type PaymentMethodSummary,
} from '../hooks/usePaymentDetails';
import { useFlag } from '../hooks/useFeatureFlags';
import { useBillingPortal } from '../hooks/useBillingPortal';

/**
 * Payment — *how am I charged?* (task 629, redesign report
 * `project/reports/innovate-app-settings-redesign.md`, mockup
 * `project/artifacts/innovate-settings-payment-mockup.html`).
 *
 * A read-only projection of the account's Stripe payment method and customer
 * billing details, rendered inside the Settings shell (task 625) under the
 * owner-only Billing group. Two facts govern the whole page:
 *
 *  1. Innovate never sees card numbers. The card summary (brand/last4/expiry)
 *     comes from Stripe through AMS (task 627's `/api/billing/payment-details`
 *     read); no card form ever renders here. Payment owns the two Stripe doors
 *     that belong to it (task 1015): "Update payment method" and "Edit billing
 *     details" open the Billing Portal straight from this page, through the
 *     shared `useBillingPortal` hook, rather than sending the User to Billing to
 *     click a second time.
 *  2. Owner-only. The Billing group doesn't render for non-owners in the shell,
 *     but a non-owner who deep-links to `/settings/payment` still lands here, so
 *     the page itself shows the managed-by-owner note and fetches nothing.
 *
 * The read may be absent until task 627 ships (the hook resolves 403/404/501 to
 * null); when so, the page shows an unavailable note rather than an error.
 */

/** The plan-catalog page an owner without a card is pointed at to get started. */
const CHOOSE_PLAN_HREF = '/settings/subscription';

/** Title-case a Stripe card brand slug ("visa" → "Visa", "american_express" → "American Express"). */
function formatBrand(brand: string): string {
  return brand
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Short brand chip label, uppercased ("visa" → "VISA"). */
function brandChipLabel(brand: string): string {
  return formatBrand(brand).toUpperCase();
}

/** Zero-pad a card expiry month ("4/2028" reads as "04/2028"). */
function formatExpiry(month: number, year: number): string {
  const mm = String(month).padStart(2, '0');
  return `${mm}/${year}`;
}

/** One-line address from the parts Stripe actually returned, skipping blanks. */
function formatAddress(address: PaymentBillingAddress | null): string | null {
  if (!address) return null;
  const cityRegion = [address.city, address.state].filter(Boolean).join(', ');
  const parts = [
    address.line1,
    address.line2,
    cityRegion || null,
    address.postalCode,
    address.country,
  ].filter((part): part is string => Boolean(part && part.trim()));
  return parts.length > 0 ? parts.join(', ') : null;
}

/** The first tax ID as a display string, or null when none is on file. */
function formatTaxId(details: PaymentBillingDetails | null): string | null {
  const taxId = details?.taxIds?.[0];
  return taxId ? taxId.value : null;
}

function PaymentMethodCard({ method }: { method: PaymentMethodSummary }) {
  return (
    <div
      className="flex max-w-lg items-center gap-4 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
      data-testid="payment-method-summary"
    >
      <div
        className="flex h-8 w-12 flex-shrink-0 items-center justify-center rounded bg-indigo-900 text-[10px] font-extrabold tracking-wide text-white"
        aria-hidden="true"
      >
        {brandChipLabel(method.brand)}
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-gray-900 dark:text-white" data-testid="payment-method-brand-last4">
          {formatBrand(method.brand)} ···· {method.last4}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="payment-method-expiry">
          Expires {formatExpiry(method.expMonth, method.expYear)}
        </p>
      </div>
      {method.isDefault && (
        <span
          className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800 dark:bg-green-900/50 dark:text-green-300"
          data-testid="payment-method-default-badge"
        >
          Default
        </span>
      )}
    </div>
  );
}

/** A labelled read-only tile in the billing-details grid. */
function DetailTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-700/40">
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">{value}</p>
      {hint && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
    </div>
  );
}

export default function PaymentPage() {
  // Billing affordances are gated by innovate.fe.billing.page, whose
  // role-owner cohort is the owner check (task 692). The role claim is no
  // longer read here: evaluation is uniform and, as a permission-type flag,
  // fails closed during an Unleash outage.
  const canManageBilling = useFlag('innovate.fe.billing.page');
  // The Stripe Billing Portal door, shared with the Subscription page. One
  // session at a time, so the failure message is rendered under whichever card
  // asked for it rather than under both.
  const { openPortal, redirecting, sessionError } = useBillingPortal();
  const [errorSource, setErrorSource] = useState<'method' | 'details' | null>(null);


  // Only the owner ever reads Stripe data; skip the call entirely otherwise.
  const { data: details, isLoading, isError } = usePaymentDetails(canManageBilling);

  const header = (
    <PageHeader
      title="Payment"
      description="The card charged for your subscription and usage, and the billing details that appear on your invoices. Payments are processed securely by Stripe — Innovate never sees your card number."
    />
  );

  // --- Non-owner: managed by the account owner --------------------------------
  if (!canManageBilling) {
    return (
      <div data-testid="payment-page">
        {header}
        <p className="mt-6 text-sm text-gray-500 dark:text-gray-400" data-testid="payment-owner-note">
          Payment and billing are managed by your account owner.
        </p>
      </div>
    );
  }

  const hasCustomer = details?.hasCustomer ?? false;
  const method = details?.paymentMethod ?? null;
  const billing = details?.billingDetails ?? null;

  const addressLine = formatAddress(billing?.address ?? null);
  const taxId = formatTaxId(billing);

  return (
    <div className="space-y-6" data-testid="payment-page">
      {header}

      {isLoading && (
        <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="payment-loading">
          Loading your payment details…
        </p>
      )}

      {/* The read failed unrecoverably (a 5xx / transport error, not a graceful absence). */}
      {isError && !isLoading && (
        <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="payment-unavailable">
          Your payment details are unavailable right now. Please try again shortly.
        </p>
      )}

      {/* The endpoint resolved but is not yet deployed (hook returned null). */}
      {!isLoading && !isError && details === null && (
        <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="payment-unavailable">
          Your payment details are unavailable right now. Please try again shortly.
        </p>
      )}

      {!isLoading && !isError && details && (
        <>
          {/* No Stripe customer yet — new or no-plan account. */}
          {!hasCustomer && (
            <section
              className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800"
              data-testid="payment-no-customer"
            >
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">No payment method yet</h2>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Your account doesn't have a plan yet, so there's no card on file. Choose a plan to get
                started — you'll add a payment method during checkout.
              </p>
              <Link
                to={CHOOSE_PLAN_HREF}
                data-testid="payment-choose-plan-link"
                className="mt-4 inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700"
              >
                Choose a plan
              </Link>
            </section>
          )}

          {hasCustomer && (
            <>
              {/* Payment method */}
              <section
                className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800"
                data-testid="payment-method-card"
              >
                <div className="sm:flex sm:items-start sm:justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">Payment method</h2>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      The card charged for your subscription and usage. Stored and processed by Stripe —
                      Innovate never sees card numbers.
                    </p>
                  </div>
                  {/* task 1015: the card lives in Stripe, so the button that
                      changes it opens Stripe's payment-method flow directly
                      from the page that shows the card. */}
                  <button
                    onClick={() => {
                      setErrorSource('method');
                      void openPortal('payment_method');
                    }}
                    disabled={redirecting}
                    data-testid="payment-update-method-button"
                    className="mt-4 inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:mt-0"
                  >
                    {redirecting ? 'Redirecting...' : 'Update payment method'}
                  </button>
                </div>

                <div className="mt-4">
                  {method ? (
                    <PaymentMethodCard method={method} />
                  ) : (
                    <p
                      className="text-sm text-gray-500 dark:text-gray-400"
                      data-testid="payment-no-card"
                    >
                      No card on file yet. Use <span className="font-medium">Update payment method</span> to
                      add one in Stripe.
                    </p>
                  )}
                </div>

                {sessionError && errorSource === 'method' && (
                  <p
                    className="mt-3 text-sm text-red-600 dark:text-red-400"
                    data-testid="payment-session-error"
                  >
                    {sessionError}
                  </p>
                )}
              </section>

              {/* Billing details */}
              <section
                className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800"
                data-testid="payment-billing-details-card"
              >
                <div className="sm:flex sm:items-start sm:justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">Billing details</h2>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Appears on every invoice.</p>
                  </div>
                  {/* No `flow` here: Stripe has no deep link for billing
                      details, so this opens the portal home rather than
                      pretending otherwise (task 1015, moved from the retired
                      Billing actions section). */}
                  <button
                    onClick={() => {
                      setErrorSource('details');
                      void openPortal();
                    }}
                    disabled={redirecting}
                    data-testid="payment-edit-billing-button"
                    className="mt-4 inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:mt-0"
                  >
                    {redirecting ? 'Redirecting...' : 'Edit billing details'}
                  </button>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <DetailTile
                    label="Billing email"
                    value={billing?.email ?? 'Not set'}
                    hint="Invoices and receipts are sent here."
                  />
                  <DetailTile
                    label="Billed to"
                    value={billing?.name ?? 'Not set'}
                    hint={addressLine ?? undefined}
                  />
                  <DetailTile
                    label="Tax ID"
                    value={taxId ?? 'Not set'}
                    hint="Applied to invoices where required."
                  />
                  <DetailTile
                    label="Currency"
                    value={details.currency ? details.currency.toUpperCase() : 'Not set'}
                    hint="Set by your plan catalog."
                  />
                </div>

                <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                  Billing details are edited on the Stripe billing home — Stripe has no direct link
                  for editing them, so that button opens the home page rather than pretending
                  otherwise.
                </p>

                {sessionError && errorSource === 'details' && (
                  <p
                    className="mt-3 text-sm text-red-600 dark:text-red-400"
                    data-testid="payment-session-error"
                  >
                    {sessionError}
                  </p>
                )}
              </section>
            </>
          )}
        </>
      )}


    </div>
  );
}
